import { MaterialIcons } from "@expo/vector-icons";
import _ from "lodash";
import {
  Actionsheet,
  Box,
  Button,
  Checkbox,
  HStack,
  Icon,
  Radio,
  ScrollView,
  Text,
  useDisclose,
  VStack,
} from "native-base";
import React, { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import CommonHeaderDivider from "../commons/CommonHeaderDivider";
import type { FoodRecipeInspirationListApiArg } from "../services/backendTypes";
import logger from "../services/logger";

// Filter types
type FavoriteFilter = "favorite" | "no_favorite" | null;
type EatingTimeFilter = FoodRecipeInspirationListApiArg["mealTypes"];
type PrepTimeFilter = string | null; // Single string or null

// --- Actionsheet component props ---
interface FilterActionsheetComponentProps {
  isOpen: boolean;
  onClose: () => void;
  t: (key: string, options?: Record<string, unknown> | string) => string;
  selectedFavorite: FavoriteFilter;
  setSelectedFavorite: React.Dispatch<React.SetStateAction<FavoriteFilter>>;
  selectedEatingTimes: EatingTimeFilter;
  setSelectedEatingTimes: React.Dispatch<React.SetStateAction<EatingTimeFilter>>;
  selectedPrepTimes: PrepTimeFilter;
  setSelectedPrepTimes: React.Dispatch<React.SetStateAction<PrepTimeFilter>>;
  applyFilters: () => void;
  clearFilters: () => void;
}

// --- Define and Export the Actionsheet component ---
// Use React.memo to prevent re-renders if props haven't changed
// Prop types are checked by TypeScript interface FilterActionsheetComponentProps
// eslint-disable-next-line react/prop-types
export const FilterActionsheetComponent: React.FC<FilterActionsheetComponentProps> = React.memo(
  ({
    // eslint-disable-next-line react/prop-types
    isOpen,
    // eslint-disable-next-line react/prop-types
    onClose,
    // eslint-disable-next-line react/prop-types
    t,
    // eslint-disable-next-line react/prop-types
    selectedFavorite,
    // eslint-disable-next-line react/prop-types
    setSelectedFavorite,
    // eslint-disable-next-line react/prop-types
    selectedEatingTimes,
    // eslint-disable-next-line react/prop-types
    setSelectedEatingTimes,
    // eslint-disable-next-line react/prop-types
    selectedPrepTimes,
    // eslint-disable-next-line react/prop-types
    setSelectedPrepTimes,
    // eslint-disable-next-line react/prop-types
    applyFilters,
    // eslint-disable-next-line react/prop-types
    clearFilters,
  }) => (
    <Actionsheet isOpen={isOpen} onClose={onClose}>
      <Actionsheet.Content bg="white">
        <Box w="100%" h={60} px={4} justifyContent="center" borderBottomWidth="1" borderColor="coolGray.200">
          <Text fontSize="lg" fontWeight="bold" color="coolGray.800" textAlign="center">
            {t("recipe_inspiration.filters_title")}
          </Text>
        </Box>
        <ScrollView style={{ width: "100%" }}>
          <VStack space={4} px={4} py={4}>
            {/* Favorite Filter */}
            <VStack space={2}>
              <Text bold>{t("recipe_inspiration.filter_favorite")}</Text>
              <Radio.Group
                name="favoriteFilterGroup"
                value={selectedFavorite ?? ""}
                onChange={(nextValue) => {
                  setSelectedFavorite((nextValue as FavoriteFilter) || null);
                }}
              >
                <Radio value="favorite" my={1}>
                  {t("recipe_inspiration.filter_favorite_yes")}
                </Radio>
                <Radio value="no_favorite" my={1}>
                  {t("recipe_inspiration.filter_favorite_no")}
                </Radio>
                <Radio value="" my={1}>
                  {t("recipe_inspiration.filter_favorite_any")}
                </Radio>
              </Radio.Group>
            </VStack>
            <CommonHeaderDivider />
            {/* Eating Time Filter */}
            <VStack space={2}>
              <Text bold>{t("recipe_inspiration.filter_eating_time")}</Text>
              <Checkbox.Group
                onChange={setSelectedEatingTimes}
                value={selectedEatingTimes ?? []}
                accessibilityLabel="Choose eating times"
              >
                <Checkbox value="BREAKFAST" my={1}>
                  {t("general.meal_types.BREAKFAST")}
                </Checkbox>
                <Checkbox value="LUNCH" my={1}>
                  {t("general.meal_types.LUNCH")}
                </Checkbox>
                <Checkbox value="DINNER" my={1}>
                  {t("general.meal_types.DINNER")}
                </Checkbox>
                <Checkbox value="SNACK" my={1}>
                  {t("general.meal_types.SNACK")}
                </Checkbox>
              </Checkbox.Group>
            </VStack>
            <CommonHeaderDivider />
            {/* Preparation Time Filter */}
            <VStack space={2}>
              <Text bold>{t("recipe_inspiration.filter_prep_time")}</Text>
              <Radio.Group
                name="prepTimeFilterGroup"
                value={selectedPrepTimes ?? ""}
                onChange={(nextValue) => {
                  setSelectedPrepTimes(nextValue || null);
                }}
                accessibilityLabel="Choose preparation time"
              >
                <Radio value="1-5" my={1}>
                  {t("recipe_inspiration.prep_time_1_5")}
                </Radio>
                <Radio value="6-10" my={1}>
                  {t("recipe_inspiration.prep_time_6_10")}
                </Radio>
                <Radio value="11-20" my={1}>
                  {t("recipe_inspiration.prep_time_11_20")}
                </Radio>
                <Radio value="21-30" my={1}>
                  {t("recipe_inspiration.prep_time_21_30")}
                </Radio>
                <Radio value="31-60" my={1}>
                  {t("recipe_inspiration.prep_time_31_60")}
                </Radio>
                <Radio value="" my={1}>
                  {t("recipe_inspiration.prep_time_any")}
                </Radio>
              </Radio.Group>
            </VStack>
          </VStack>
        </ScrollView>
        <Box w="100%" px={4} pb={4} pt={2} borderTopWidth="1" borderColor="coolGray.200">
          <HStack justifyContent="space-between">
            <Button variant="ghost" onPress={clearFilters} testID="clear-filters-button">
              {t("general.clear")}
            </Button>
            <Button onPress={applyFilters} testID="apply-filters-button">
              {t("general.apply")}
            </Button>
          </HStack>
        </Box>
      </Actionsheet.Content>
    </Actionsheet>
  )
);
FilterActionsheetComponent.displayName = "FilterActionsheetComponent";

// --- Hook return type ---
interface UseRecipeInspirationFiltersResult {
  filterQueryArgs: Omit<FoodRecipeInspirationListApiArg, "page" | "pageSize" | "ingredientSearch">;
  openFilterSheet: () => void;
  // Expose state, setters, and actions needed by the Actionsheet component
  actionsheetProps: Omit<FilterActionsheetComponentProps, "t">; // Exclude t, as it's from the parent's scope
  t: (key: string, options?: Record<string, unknown> | string) => string; // Pass t through for convenience
}

// --- Custom Hook ---
export const useRecipeInspirationFilters = (
  onFiltersApplied: () => void // Callback when filters are applied/cleared
): UseRecipeInspirationFiltersResult => {
  const { t } = useTranslation();
  const { isOpen, onOpen, onClose } = useDisclose();

  // Filter State
  const [selectedFavorite, setSelectedFavorite] = useState<FavoriteFilter>(null);
  const [selectedEatingTimes, setSelectedEatingTimes] = useState<EatingTimeFilter>([]);
  const [selectedPrepTimes, setSelectedPrepTimes] = useState<PrepTimeFilter>(null);

  // Helper to parse prep time ranges (memoized)
  const getPrepTimeBounds = useCallback(
    (range: PrepTimeFilter): { gte: number | undefined; lte: number | undefined } => {
      if (!range) return { gte: undefined, lte: undefined };
      const parts = range.split("-");
      const min = parseInt(parts[0] ?? "", 10);
      const max = parseInt(parts[1] ?? "", 10);
      return {
        gte: !Number.isNaN(min) ? min : undefined,
        lte: !Number.isNaN(max) ? max : undefined,
      };
    },
    []
  );

  // Memoized filter arguments for the API query
  const filterQueryArgs = useMemo(() => {
    const prepTimeBounds = getPrepTimeBounds(selectedPrepTimes);
    const params: Omit<FoodRecipeInspirationListApiArg, "page" | "pageSize" | "ingredientSearch"> = {
      favourite: selectedFavorite === null ? undefined : selectedFavorite === "favorite",
      mealTypes: selectedEatingTimes && selectedEatingTimes.length > 0 ? selectedEatingTimes : undefined,
      preparationTimeMinGte: prepTimeBounds.gte,
      preparationTimeMinLte: prepTimeBounds.lte,
    };
    return _.omitBy(params, (value) => _.isUndefined(value));
  }, [selectedFavorite, selectedEatingTimes, selectedPrepTimes, getPrepTimeBounds]);

  // Memoize Actions callbacks to ensure stable props for FilterActionsheetComponent
  const applyFilters = useCallback(() => {
    onFiltersApplied(); // Trigger refetch in parent
    onClose();
    // Wrap logger call in arrow function to fix unbound-method error
    (() => logger.info("Applying filters:", { selectedFavorite, selectedEatingTimes, selectedPrepTimes }))();
  }, [onFiltersApplied, onClose, selectedFavorite, selectedEatingTimes, selectedPrepTimes]);

  const clearFilters = useCallback(() => {
    setSelectedFavorite(null);
    setSelectedEatingTimes([]);
    setSelectedPrepTimes(null);
    onFiltersApplied(); // Trigger refetch in parent
    onClose();
    // Wrap logger call in arrow function to fix unbound-method error
    (() => logger.info("Filters cleared, triggering refetch."))();
  }, [onFiltersApplied, onClose]);

  // Group props for the Actionsheet component (excluding t)
  // State setters are stable, callbacks are memoized
  const actionsheetProps = useMemo(
    () => ({
      isOpen,
      onClose,
      selectedFavorite,
      setSelectedFavorite,
      selectedEatingTimes,
      setSelectedEatingTimes,
      selectedPrepTimes,
      setSelectedPrepTimes,
      applyFilters,
      clearFilters,
    }),
    [isOpen, onClose, selectedFavorite, selectedEatingTimes, selectedPrepTimes, applyFilters, clearFilters]
  );

  return {
    filterQueryArgs,
    openFilterSheet: onOpen,
    actionsheetProps, // Return the props object
    t, // Pass t function from this hook instance
  };
};
